import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { getResendAvailability } from '@/services/apis/mfa-orchestrator.api';
import { logError } from '@/utils/error-logger';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

// A login flow lives minutes, so a deadline a day out is the never sentinel.
const RESEND_NEVER_THRESHOLD_MS = 24 * 60 * 60 * MS_PER_SECOND;

const EXHAUSTED = -1;
const READY = 0;

const parseDeadline = (deadline?: string) => {
  if (!deadline) {
    return null;
  }

  const parsed = new Date(deadline).getTime();

  return Number.isNaN(parsed) ? null : parsed;
};

// A snapshot that holds still while idle keeps the screen from re-rendering.
const readCountdown = (deadline: number | null) => {
  if (deadline === null) {
    return READY;
  }

  const remainingMs = deadline - Date.now();

  if (remainingMs > RESEND_NEVER_THRESHOLD_MS) {
    return EXHAUSTED;
  }

  return remainingMs <= 0 ? READY : Math.ceil(remainingMs / MS_PER_SECOND);
};

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  const remainingSeconds = seconds % SECONDS_PER_MINUTE;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
};

const laterDeadline = (a?: string, b?: string) => {
  const parsedA = parseDeadline(a);
  const parsedB = parseDeadline(b);

  if (parsedA === null) {
    return b;
  }

  if (parsedB === null) {
    return a;
  }

  return parsedB > parsedA ? b : a;
};

// A future deadline, or the never sentinel, is a live answer. Anything else
// could be an elapsed one a reload replayed out of navigation state.
const isBinding = (deadline?: string) =>
  readCountdown(parseDeadline(deadline)) !== READY;

// A re-executed step and a resend response each report a deadline, and they
// arrive in either order, so the later one is the one still standing.
export const useResendDeadline = (stepDeadline?: string) => {
  const [resendDeadline, setResendDeadline] = useState<string>();
  const [hasReadBudget, setHasReadBudget] = useState(false);
  const deadline = laterDeadline(stepDeadline, resendDeadline);

  // Step metadata is whatever the last navigation captured, so a reload after a
  // resend replays a deadline the resend already superseded. Reading the budget
  // once per mount is the only answer that is current, and a resend reports its
  // own deadline afterwards, so one read covers the screen's whole life.
  useEffect(() => {
    getResendAvailability()
      .then(fetched =>
        setResendDeadline(previous => laterDeadline(previous, fetched))
      )
      .catch(error => logError(error, 'mfa:resend-availability'))
      .finally(() => setHasReadBudget(true));
  }, []);

  return {
    deadline,
    setDeadline: setResendDeadline,
    // A binding deadline is safe to show before the read lands, since staleness
    // can only make it earlier. An elapsed one may be stale, so gate on it —
    // and a failed read falls through to ready.
    isResolving: !hasReadBudget && !isBinding(deadline),
  };
};

export const useResendCountdown = (nextResendAt?: string) => {
  const deadline = parseDeadline(nextResendAt);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (readCountdown(deadline) <= READY) {
        return () => undefined;
      }

      const interval = setInterval(() => {
        onStoreChange();

        if (readCountdown(deadline) <= READY) {
          clearInterval(interval);
        }
      }, MS_PER_SECOND);

      return () => clearInterval(interval);
    },
    [deadline]
  );

  const getSnapshot = useCallback(() => readCountdown(deadline), [deadline]);
  const countdown = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const secondsRemaining = Math.max(countdown, READY);

  return {
    canResend: countdown === READY,
    isExhausted: countdown === EXHAUSTED,
    secondsRemaining,
    formattedTime: formatTime(secondsRemaining),
  };
};
